import { inject, Injectable } from '@angular/core';
import { TranslateService, TranslateStore } from '@ngx-translate/core';
import { Translation } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { take } from 'rxjs';

/** Pseudo-language: empty catalog + no fallback → UI shows translation keys. */
export const DEV_TRANSLATE_LANG = 'dev';

/** localStorage key shared by header, auth, and layout config bar. */
export const APP_LANGUAGE_STORAGE_KEY = 'currentLanguage';

/** Legacy key; read-only fallback in getStoredLanguage() for older sessions. */
const LEGACY_LANGUAGE_STORAGE_KEY = 'currentLanguageDemo';

@Injectable({ providedIn: 'root' })
export class AppTranslateLanguageService {
  private readonly translate = inject(TranslateService);
  private readonly store = inject(TranslateStore);
  private readonly primeNG = inject(PrimeNG);

  /**
   * PrimeNG calendar / filter labels follow the `primeng` object in each locale JSON
   * (same pattern as EmployeePortal `TranslateService.get('primeng')` → `setTranslation`).
   */
  syncPrimeNgTranslations(): void {
    this.translate.get('primeng').subscribe((res) => {
      if (res && typeof res === 'object' && !Array.isArray(res) && Object.keys(res).length > 0) {
        this.primeNG.setTranslation(res as Translation);
      }
    });
  }

  getStoredLanguage(): string {
    // localStorage is browser-only; default when unavailable (SSR/tests).
    if (typeof localStorage === 'undefined') {
      return 'tr';
    }
    return (
      localStorage.getItem(APP_LANGUAGE_STORAGE_KEY) ??
      localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY) ??
      'tr'
    );
  }

  /** Prefer explicit local choice; otherwise fall back to server user-env language. */
  resolveLanguagePreference(stored: string | null, serverLanguage: string | undefined): string | undefined {
    if (stored) {
      return stored;
    }
    return serverLanguage;
  }

  /** Apply persisted language when it differs from the active TranslateService lang. */
  restoreStoredLanguage(): void {
    const stored = this.getStoredLanguage();
    if (stored && stored !== this.translate.getCurrentLang()) {
      this.useLanguage(stored);
    }
  }

  /**
   * Switch UI language. For `dev`, sets fallback to itself so keys are never
   * resolved from a real language file — the UI shows raw translation keys.
   */
  useLanguage(lang: string): void {
    // localStorage is browser-only; skip persistence in SSR/tests so translate.use still runs.
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, lang);
    }
    if (lang === DEV_TRANSLATE_LANG) {
      this.store.setFallbackLang(DEV_TRANSLATE_LANG, true);
    } else {
      void this.translate.setFallbackLang('en');
    }
    void this.translate
      .use(lang)
      .pipe(take(1))
      .subscribe({
        next: () => this.syncPrimeNgTranslations(),
        error: () => {},
      });
  }
}
